import {
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  HStack,
  IconButton,
  Link as ChakraLink,
  useColorModeValue,
  useDisclosure,
} from "@chakra-ui/react";
import { motion, useMotionValueEvent, useScroll } from "framer-motion";
import { useRef, useState } from "react";
import { HiMenuAlt3 } from "react-icons/hi";
import { sections } from "../data/sections";
import { useActiveSection } from "../hooks/useActiveSection";

const MotionBox = motion(Box);

const navItems = sections.map((s) => ({ href: `#${s.id}`, label: s.label }));
const sectionIds = sections.map((s) => s.id);

export function Navbar() {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const activeId = useActiveSection(sectionIds);
  const [hidden, setHidden] = useState(false);
  const bg = useColorModeValue("rgba(255,255,255,0.72)", "rgba(20,20,25,0.72)");
  const border = useColorModeValue("blackAlpha.100", "whiteAlpha.200");
  const linkColor = useColorModeValue("graphite.700", "graphite.300");
  const linkActive = useColorModeValue("graphite.900", "white");
  const pillBg = useColorModeValue("blackAlpha.100", "whiteAlpha.100");
  const shadow = useColorModeValue(
    "0 8px 30px rgba(20,20,25,0.08)",
    "0 8px 30px rgba(0,0,0,0.45)"
  );

  const { scrollY } = useScroll();
  const lastY = useRef(0);

  useMotionValueEvent(scrollY, "change", (y) => {
    const goingDown = y > lastY.current;
    setHidden(goingDown && y > 160);
    lastY.current = y;
  });

  const NavLinks = ({ onNavigate }: { onNavigate?: () => void }) => (
    <>
      {navItems.map((item) => {
        const isActive = item.href.slice(1) === activeId;
        return (
          <Box key={item.href} position="relative">
            {isActive ? (
              <MotionBox
                layoutId="nav-pill"
                position="absolute"
                inset={0}
                borderRadius="full"
                bg={pillBg}
                transition={{ type: "spring", stiffness: 380, damping: 32 }}
              />
            ) : null}
            <ChakraLink
              href={item.href}
              position="relative"
              display="block"
              px={4}
              py={2}
              fontWeight="500"
              fontSize="sm"
              color={isActive ? linkActive : linkColor}
              _hover={{ color: linkActive, textDecoration: "none" }}
              onClick={onNavigate}
            >
              {item.label}
            </ChakraLink>
          </Box>
        );
      })}
    </>
  );

  return (
    <Box as="header" position="fixed" top={0} left={0} right={0} zIndex={50} px={{ base: 3, md: 6 }} pt={{ base: 3, md: 5 }}>
      <MotionBox
        maxW="container.lg"
        mx="auto"
        animate={{ y: hidden ? -96 : 0, opacity: hidden ? 0 : 1 }}
        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
      >
        <Flex
          bg={bg}
          backdropFilter="blur(18px) saturate(160%)"
          borderRadius="full"
          borderWidth="1px"
          borderColor={border}
          boxShadow={shadow}
          px={{ base: 4, md: 3 }}
          py={2}
          align="center"
          justify="space-between"
        >
          <ChakraLink
            href="#inicio"
            fontFamily="accent"
            fontStyle="italic"
            fontSize="xl"
            fontWeight="500"
            pl={2}
            minH="44px"
            display="inline-flex"
            alignItems="center"
            color="accent.300"
            _hover={{ textDecoration: "none", opacity: 0.85 }}
          >
            Cristian
          </ChakraLink>

          <HStack spacing={0} display={{ base: "none", lg: "flex" }}>
            <NavLinks />
          </HStack>

          <HStack spacing={2}>
            <Button
              as="a"
              href="#contato"
              display={{ base: "none", md: "inline-flex" }}
              size="sm"
              borderRadius="full"
              colorScheme="accent"
            >
              Falar comigo
            </Button>
            <IconButton
              display={{ base: "flex", lg: "none" }}
              aria-label="Abrir menu"
              icon={<HiMenuAlt3 />}
              variant="ghost"
              borderRadius="full"
              onClick={onOpen}
            />
          </HStack>
        </Flex>
      </MotionBox>

      <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="xs">
        <DrawerOverlay />
        <DrawerContent bg={useColorModeValue("white", "graphite.950")}>
          <DrawerCloseButton />
          <DrawerHeader>Navegação</DrawerHeader>
          <DrawerBody>
            <Flex direction="column" gap={4} align="stretch">
              <NavLinks onNavigate={onClose} />
              <Button as="a" href="#contato" colorScheme="accent" onClick={onClose}>
                Falar comigo
              </Button>
            </Flex>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </Box>
  );
}
